import { useRef, useState } from 'react'
import { FocusTrapDialog } from '../../../components/FocusTrapDialog'
import { InlineAlert } from '../../../components/InlineAlert'
import { ApiError } from '../../../api/problem'
import { createProgram } from '../../../api/cartridgeStudio'
import type { ProgramView } from '../../../types/cartridgeStudio'

const LEVELS = ['national', 'federal', 'state', 'provincial']
const KINDS = ['rnd', 'film']

export interface NewProgramDialogProps {
  onClose: () => void
  onCreated: (program: ProgramView) => void
}

/**
 * `POST /api/v1/programs` (design doc §3 — a "should-have, not a must-have" CTA, included because
 * the endpoint is real/merged and "every cross-case list carries a create path" is house style).
 * `CreateProgramRequest{jurisdiction, level, parentProgramId?, code, kind}` — a repeat
 * `(jurisdiction, level, code)` is a real `409 program_unique_identity`, surfaced inline, never a
 * silent failure.
 */
export function NewProgramDialog({ onClose, onCreated }: NewProgramDialogProps) {
  const [jurisdiction, setJurisdiction] = useState('')
  const [level, setLevel] = useState('state')
  const [code, setCode] = useState('')
  const [kind, setKind] = useState('film')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const jurisdictionRef = useRef<HTMLInputElement>(null)

  const isValid = jurisdiction.trim() !== '' && code.trim() !== ''

  async function handleSubmit() {
    if (!isValid || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const program = await createProgram({ jurisdiction: jurisdiction.trim(), level, code: code.trim(), kind })
      onCreated(program)
    } catch (err) {
      setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not create this program. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <FocusTrapDialog
      titleId="new-program-title"
      title="New program"
      onClose={onClose}
      initialFocusRef={jurisdictionRef}
      footer={
        <>
          <button type="button" className="iw-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="iw-btn iw-btn--primary" onClick={() => void handleSubmit()} disabled={!isValid || submitting}>
            {submitting ? 'Creating…' : 'Create program'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit()
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}
      >
        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-program-jurisdiction">
            Jurisdiction
          </label>
          <input
            id="new-program-jurisdiction"
            ref={jurisdictionRef}
            className="iw-input"
            value={jurisdiction}
            required
            onChange={(e) => setJurisdiction(e.target.value)}
            placeholder="e.g. Georgia"
          />
        </div>

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-program-level">
            Level
          </label>
          <select id="new-program-level" className="iw-input" value={level} onChange={(e) => setLevel(e.target.value)}>
            {LEVELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </div>

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-program-code">
            Program code
          </label>
          <input
            id="new-program-code"
            className="iw-input"
            value={code}
            required
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. GA-FILM"
          />
        </div>

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="new-program-kind">
            Domain
          </label>
          <select id="new-program-kind" className="iw-input" value={kind} onChange={(e) => setKind(e.target.value)}>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {k === 'rnd' ? 'R&D' : 'Film & Media'}
              </option>
            ))}
          </select>
        </div>
      </form>
    </FocusTrapDialog>
  )
}
